import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, X, Send, Loader2, BookMarked, RotateCw } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import VoiceButton from "./VoiceButton";
import SaveButton from "./SaveButton";

const BRAND = "https://customer-assets.emergentagent.com/job_1a752c50-99ae-44d4-a5d5-08c385e2832e/artifacts/enaxu6ve_Product%402x.png";

/**
 * PradhanChat — floating chat widget for Ms. Pradhan.
 * Mounted globally in Layout.  Only shows when the user is signed in.
 */
export default function PradhanChat() {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [threadId, setThreadId] = useState(null);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [regenIdx, setRegenIdx] = useState(null);   // index of the assistant msg currently being regenerated
  const scrollRef = useRef(null);

  useEffect(() => {
    if (!user || !open || loaded) return;
    api.get("/pradhan/history").then(({ data }) => {
      setThreadId(data.thread_id || null);
      setMessages(data.messages || []);
      setLoaded(true);
    }).catch(() => setLoaded(true));
  }, [user, open, loaded]);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages, busy]);

  // External "Ask Ms. Pradhan" trigger — LessonDetail (and other pages) can
  // fire `window.dispatchEvent(new CustomEvent("pradhan:open", {detail:{message}}))`
  // to pop the panel and auto-send a pre-filled question. Must live above
  // the early `if (!user) return null` so hook order is stable.
  useEffect(() => {
    if (!user) return;
    const onOpen = (ev) => {
      setOpen(true);
      const msg = ev?.detail?.message;
      if (msg) {
        // Give the panel a tick to mount + history to load, then send.
        setTimeout(() => { sendMessage(msg); }, 250);
      }
    };
    window.addEventListener("pradhan:open", onOpen);
    return () => window.removeEventListener("pradhan:open", onOpen);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, threadId, busy]);

  if (!user) return null;

  const sendMessage = async (text) => {
    text = (text || "").trim();
    if (!text || busy) return;
    setInput("");
    const now = new Date().toISOString();
    setMessages((m) => [...m, { role: "user", content: text, at: now }]);
    setBusy(true);
    try {
      const { data } = await api.post("/pradhan/chat", { message: text, thread_id: threadId || undefined });
      setThreadId(data.thread_id);
      setMessages((m) => [...m, { role: "assistant", content: data.reply, at: data.at, citations: data.citations || [] }]);
    } catch (err) {
      setMessages((m) => [...m, { role: "assistant", content: "I'm sorry — the roadside phone dropped. Try again in a moment.", at: new Date().toISOString(), error: true }]);
    } finally { setBusy(false); }
  };

  const send = async (e) => {
    e.preventDefault();
    await sendMessage(input);
  };

  const regenerate = async (idx, tone = "warmer") => {
    if (!threadId || busy || regenIdx !== null) return;
    setRegenIdx(idx);
    try {
      const { data } = await api.post("/pradhan/regenerate", { thread_id: threadId, tone });
      setMessages((m) => {
        const next = [...m];
        if (next[idx] && next[idx].role === "assistant") {
          next[idx] = { role: "assistant", content: data.reply, at: data.at, citations: data.citations || [], regenerated: true };
        }
        return next;
      });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("regen failed", err);
    } finally { setRegenIdx(null); }
  };

  const newThread = () => {
    setThreadId(null);
    setMessages([]);
  };

  return (
    <>
      {/* Floating trigger button */}
      {!open && (
        <button
          onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 z-40 w-14 h-14 rounded-full bg-ink text-cream shadow-[6px_6px_0_0_#F4A261] border-2 border-ink flex items-center justify-center hover:scale-105 transition-transform"
          aria-label="Chat with Ms. Pradhan"
          data-testid="pradhan-chat-open"
        >
          <MessageCircle className="w-6 h-6" />
        </button>
      )}

      {/* Chat panel */}
      {open && (
        <div
          className="fixed bottom-5 right-5 z-40 w-[380px] max-w-[calc(100vw-2rem)] h-[560px] max-h-[calc(100vh-2rem)] rounded-3xl bg-cream border-2 border-ink shadow-[10px_10px_0_0_#1A1B41] flex flex-col overflow-hidden"
          data-testid="pradhan-chat-panel"
        >
          {/* Header */}
          <div className="p-3 flex items-center justify-between gap-2 border-b-2 border-ink bg-saffron/20">
            <div className="flex items-center gap-2 min-w-0">
              <img src={BRAND} alt="" className="w-9 h-9 rounded-full border-2 border-ink object-cover" />
              <div className="min-w-0">
                <div className="font-heading text-ink font-semibold text-sm">Ms. Pradhan</div>
                <div className="text-[10px] uppercase tracking-widest text-ink2 font-bold">Teacher-in-residence</div>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <button onClick={newThread} className="text-[10px] uppercase tracking-widest font-bold text-ink2 hover:text-marigold px-2" data-testid="pradhan-chat-new">
                New thread
              </button>
              <button onClick={() => setOpen(false)} className="w-8 h-8 rounded-full hover:bg-ink/10 flex items-center justify-center" aria-label="Close" data-testid="pradhan-chat-close">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {/* Messages */}
          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-3" data-testid="pradhan-chat-messages">
            {messages.length === 0 && !busy && (
              <div className="text-center py-8">
                <img src={BRAND} alt="" className="w-16 h-16 mx-auto rounded-full border-2 border-ink mb-3" />
                <p className="font-hand text-2xl text-marigold leading-tight">&ldquo;Ready when you are, dear.&rdquo;</p>
                <p className="text-xs text-ink2 mt-3">Ask me about programming, Apple development, algorithms, or the road-trip lessons.</p>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[80%] rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed whitespace-pre-wrap ${
                  m.role === "user"
                    ? "bg-ink text-cream rounded-br-sm"
                    : m.error
                      ? "bg-marigold/10 border border-marigold/30 text-ink rounded-bl-sm"
                      : "bg-paper border border-border text-ink rounded-bl-sm"
                }`}>
                  {m.content}
                  {m.role === "assistant" && !m.error && Array.isArray(m.citations) && m.citations.length > 0 && (
                    <div className="mt-2.5 pt-2.5 border-t border-royal/15 space-y-1" data-testid={`pradhan-msg-citations-${i}`}>
                      <div className="text-[9px] uppercase tracking-widest font-bold text-royal/70 inline-flex items-center gap-1">
                        <BookMarked className="w-3 h-3 text-gold" /> Curriculum sources
                      </div>
                      {m.citations.map((c) => (
                        <div key={c.slug} className="flex items-start gap-2 group" data-testid={`pradhan-msg-citation-${c.code}`}>
                          <Link
                            to={`/library/${c.slug}`}
                            onClick={() => setOpen(false)}
                            className="flex-1 min-w-0 text-[11px] text-royal hover:text-marigold transition-colors"
                          >
                            <span className="font-mono text-[10px] text-royal/60 mr-1.5">{c.code}</span>
                            <span className="group-hover:underline">{c.title}</span>
                          </Link>
                          <SaveButton
                            slug={c.slug}
                            title={c.title}
                            size="sm"
                            testId={`pradhan-msg-save-${c.code}`}
                            className="flex-shrink-0"
                          />
                        </div>
                      ))}
                    </div>
                  )}
                  {m.role === "assistant" && !m.error && (
                    <div className="mt-2 -mb-1 -ml-1 flex items-center gap-1 flex-wrap">
                      <VoiceButton text={m.content} label="Listen" data-testid={`pradhan-msg-voice-${i}`} />
                      <button
                        type="button"
                        onClick={() => regenerate(i, "warmer")}
                        disabled={busy || regenIdx !== null}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] uppercase tracking-widest font-bold text-royal/70 hover:text-royal hover:bg-royal/5 transition-colors disabled:opacity-40"
                        title="Ask Ms. Pradhan for a warmer, more concrete answer"
                        data-testid={`pradhan-msg-regen-${i}`}
                      >
                        {regenIdx === i
                          ? <><Loader2 className="w-3 h-3 animate-spin" /> Rewriting…</>
                          : <><RotateCw className="w-3 h-3" /> Try again</>}
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))}
            {busy && (
              <div className="flex justify-start">
                <div className="max-w-[80%] rounded-2xl rounded-bl-sm px-3.5 py-2.5 bg-paper border border-border text-ink2 text-sm inline-flex items-center gap-2">
                  <Loader2 className="w-3.5 h-3.5 animate-spin" /> Ms. Pradhan is writing…
                </div>
              </div>
            )}
          </div>

          {/* Input */}
          <form onSubmit={send} className="p-2.5 border-t-2 border-ink flex gap-2 bg-cream" data-testid="pradhan-chat-form">
            <input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Ask Ms. Pradhan…"
              className="flex-1 px-3 py-2 rounded-full border-2 border-ink bg-cream text-sm focus:outline-none focus:border-marigold"
              maxLength={1200}
              data-testid="pradhan-chat-input"
            />
            <button type="submit" disabled={busy || !input.trim()} className="w-10 h-10 rounded-full bg-ink text-cream flex items-center justify-center hover:bg-marigold transition-colors disabled:opacity-40" data-testid="pradhan-chat-send">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
